import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Clock, TrendingUp, Tag, Disc, Wrench, Layers, Package } from "lucide-react";
import { fetchSearchSuggest, fetchSearchPopular } from "../lib/api";
import { getRecentSearches, pushRecentSearch, clearRecentSearches } from "../lib/recentSearches";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Storefront autosuggest dropdown.
 *
 * Behaviour:
 *  - Empty input → recent searches (from localStorage) + popular fallback
 *    (top sizes, brands, tyres and emergency tools fetched once).
 *  - Typed input → debounced call to /api/search/suggest. Server-side tokeniser
 *    handles size patterns like "205/55 R16" and brand/model matching.
 *  - Arrow keys move selection, Enter navigates, Escape closes.
 *  - Click outside closes the panel.
 */

const ICONS = {
  size: <Disc size={16} />,
  brand: <Tag size={16} />,
  tyre: <Disc size={16} />,
  tool: <Wrench size={16} />,
  part: <Package size={16} />,
  "part-category": <Layers size={16} />,
};

const GROUP_LABELS = {
  sizes: "Tyre Sizes",
  part_categories: "Part Categories",
  brands: "Brands",
  parts: "Auto Parts",
  tyres: "Tyres",
  tools: "Emergency Tools",
};

const GROUP_ORDER = ["sizes", "part_categories", "brands", "parts", "tyres", "tools"];

const flattenGroups = (groups) => {
  if (!groups) return [];
  return GROUP_ORDER.flatMap((key) => (groups[key] || []).map((item) => ({ ...item, _group: key })));
};

const SearchAutosuggest = ({ onClose, compact = false }) => {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState(null);
  const [popular, setPopular] = useState(null);
  const [recent, setRecent] = useState(getRecentSearches());
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Focus + popular fetch on mount
  useEffect(() => {
    inputRef.current?.focus();
    fetchSearchPopular(4).then((r) => setPopular(r.groups)).catch(() => setPopular(null));
  }, []);

  // Debounced suggest fetch
  useEffect(() => {
    const trimmed = q.trim();
    if (!trimmed) {
      setGroups(null);
      setLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetchSearchSuggest(trimmed, 8);
        setGroups(r.groups);
      } catch {
        setGroups(null);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Build the currently-displayed flat list (used for keyboard nav)
  const displayItems = useMemo(() => {
    if (q.trim()) return flattenGroups(groups);
    const recentItems = (recent || []).map((r) => ({ ...r, _group: "recent" }));
    const popularItems = flattenGroups(popular);
    return [...recentItems, ...popularItems];
  }, [q, groups, recent, popular]);

  useEffect(() => { setActiveIdx(-1); }, [displayItems.length]);

  const choose = (item) => {
    if (!item) return;
    pushRecentSearch({ type: item.type, label: item.label, sublabel: item.sublabel, href: item.href, image: item.image });
    setRecent(getRecentSearches());
    onClose?.();
    navigate(item.href);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(displayItems.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && displayItems[activeIdx]) {
        choose(displayItems[activeIdx]);
      } else if (displayItems.length > 0) {
        // No selection → pick the top suggestion (size > brand > tyre > tool)
        choose(displayItems[0]);
      } else if (q.trim()) {
        // No suggestions at all → land on tyres list as a last-resort fallback
        onClose?.();
        navigate("/tyres");
      }
    } else if (e.key === "Escape") {
      onClose?.();
    }
  };

  const showingResults = q.trim().length > 0;

  return (
    <div className="vd-autosuggest" data-testid="search-autosuggest">
      <div className="vd-as-input-wrap">
        <Search size={18} color="var(--text-muted)" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Search tyres, sizes, brands or tools…"
          className="vd-as-input"
          aria-label="Search"
          data-testid="search-autosuggest-input"
        />
        {q && (
          <button onClick={() => setQ("")} className="vd-as-clear" aria-label="Clear" data-testid="search-autosuggest-clear">
            <X size={16} />
          </button>
        )}
        {onClose && (
          <button onClick={onClose} className="vd-as-close" aria-label="Close search" data-testid="search-autosuggest-close">
            <X size={18} />
          </button>
        )}
      </div>

      <div className="vd-as-panel" data-testid="search-autosuggest-panel">
        {loading && showingResults && (
          <div className="vd-as-loading" data-testid="search-autosuggest-loading">Searching…</div>
        )}

        {showingResults && !loading && displayItems.length === 0 && (
          <div className="vd-as-empty" data-testid="search-autosuggest-empty">
            <p className="vd-heading font-semibold">No matches</p>
            <p className="vd-muted text-sm">Try a size like “205/55 R16” or a brand name.</p>
          </div>
        )}

        {/* Recent (only when not searching) */}
        {!showingResults && recent.length > 0 && (
          <SuggestGroup
            title="Recent"
            icon={<Clock size={14} />}
            startIdx={0}
            items={recent.map((r) => ({ ...r, _group: "recent" }))}
            activeIdx={activeIdx}
            onPick={choose}
            extra={
              <button
                className="vd-as-link"
                onClick={() => { clearRecentSearches(); setRecent([]); }}
                data-testid="search-autosuggest-clear-recent"
              >Clear</button>
            }
          />
        )}

        {/* Popular fallback — reuses the same live-groups renderer for consistency */}
        {!showingResults && popular && (
          <PopularGroups groups={popular} startIdx={recent.length} activeIdx={activeIdx} onPick={choose} />
        )}

        {/* Live results */}
        {showingResults && groups && (
          <LiveGroups groups={groups} activeIdx={activeIdx} onPick={choose} />
        )}
      </div>

      <style>{`
        .vd-autosuggest { width: 100%; }
        .vd-as-input-wrap {
          display: flex; align-items: center; gap: 10px;
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 10px;
          padding: ${compact ? "10px 14px" : "14px 18px"};
          box-shadow: 0 6px 24px rgba(0,0,0,0.06);
        }
        .vd-as-input { flex: 1; background: transparent; border: 0; outline: 0; color: var(--text-heading); font-size: ${compact ? "15px" : "17px"}; }
        .vd-as-input::placeholder { color: var(--text-muted); }
        .vd-as-clear, .vd-as-close { background: transparent; border: 0; cursor: pointer; color: var(--text-muted); padding: 4px; }
        .vd-as-clear:hover, .vd-as-close:hover { color: var(--text-heading); }
        .vd-as-panel { margin-top: 8px; max-height: 70vh; overflow-y: auto; }
        .vd-as-loading, .vd-as-empty { padding: 18px 8px; }
        .vd-as-empty p { margin: 0; }
        .vd-as-link { background: transparent; border: 0; color: var(--primary-red); font-size: 12px; font-weight: 600; cursor: pointer; }
      `}</style>
    </div>
  );
};

const SuggestGroup = ({ title, icon, items, startIdx, activeIdx, onPick, extra }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="vd-as-group" data-testid={`search-group-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="vd-as-group-head">
        <span className="vd-as-group-title">{icon}<span>{title}</span></span>
        {extra}
      </div>
      <ul className="vd-as-list">
        {items.map((item, i) => {
          const idx = startIdx + i;
          return (
            <SuggestRow key={`${item.href}-${i}`} item={item} active={activeIdx === idx} onPick={onPick} idx={idx} />
          );
        })}
      </ul>
      <style>{`
        .vd-as-group { padding: 10px 4px; border-top: 1px solid var(--border-color); }
        .vd-as-group:first-child { border-top: 0; }
        .vd-as-group-head { display: flex; align-items: center; justify-content: space-between; padding: 0 8px 6px; }
        .vd-as-group-title { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); }
        .vd-as-list { list-style: none; margin: 0; padding: 0; }
      `}</style>
    </div>
  );
};

// Icon shown to the left of each group heading in the live results list.
// Chosen per-group so the categorisation is visually obvious at a glance.
const GROUP_ICONS = {
  sizes: <Disc size={14} />,
  part_categories: <Layers size={14} />,
  brands: <Tag size={14} />,
  parts: <Package size={14} />,
  tyres: <Disc size={14} />,
  tools: <Wrench size={14} />,
};

// Both live and popular groups render the same list of buckets — the only
// differences are the item source object (`popular` vs `groups`) and the
// leading heading icon (TrendingUp for popular).
const _renderGroups = (groups, activeIdx, onPick, headingIcon, startIdx = 0) => {
  let cursor = startIdx;
  const blocks = [];
  GROUP_ORDER.forEach((key) => {
    const items = (groups && groups[key]) || [];
    if (items.length === 0) return;
    blocks.push(
      <SuggestGroup
        key={key}
        title={GROUP_LABELS[key]}
        icon={headingIcon || GROUP_ICONS[key]}
        startIdx={cursor}
        items={items.map((x) => ({ ...x, _group: key }))}
        activeIdx={activeIdx}
        onPick={onPick}
      />
    );
    cursor += items.length;
  });
  return blocks;
};

const LiveGroups = ({ groups, activeIdx, onPick }) =>
  <>{_renderGroups(groups, activeIdx, onPick, null, 0)}</>;

const PopularGroups = ({ groups, startIdx, activeIdx, onPick }) =>
  <>{_renderGroups(groups, activeIdx, onPick, <TrendingUp size={14} />, startIdx)}</>;

const SuggestRow = ({ item, active, onPick, idx }) => {
  const icon = ICONS[item.type] || <Search size={16} />;
  const imageSrc = item.image
    ? (item.image.startsWith("http") ? item.image : `${BACKEND_URL}${item.image}`)
    : null;
  return (
    <li>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); onPick(item); }}
        className={`vd-as-row ${active ? "active" : ""}`}
        data-testid={`search-suggest-item-${idx}`}
        data-suggest-type={item.type}
      >
        <span className="vd-as-row-icon">
          {imageSrc ? (
            <img src={imageSrc} alt="" loading="lazy" />
          ) : icon}
        </span>
        <span className="vd-as-row-text">
          <span className="vd-as-row-label">{item.label}</span>
          {item.sublabel && <span className="vd-as-row-sub">{item.sublabel}</span>}
        </span>
      </button>
      <style>{`
        .vd-as-row { width: 100%; display: flex; align-items: center; gap: 12px; padding: 10px 8px; border-radius: 8px; background: transparent; border: 0; cursor: pointer; text-align: left; color: var(--text-heading); transition: background-color 120ms ease; }
        .vd-as-row:hover, .vd-as-row.active { background: var(--bg-secondary); }
        .vd-as-row-icon { width: 36px; height: 36px; flex-shrink: 0; border-radius: 8px; overflow: hidden; display: inline-flex; align-items: center; justify-content: center; background: var(--bg-secondary); color: var(--primary-red); }
        .vd-as-row-icon img { width: 100%; height: 100%; object-fit: cover; }
        .vd-as-row-text { display: flex; flex-direction: column; min-width: 0; flex: 1; }
        .vd-as-row-label { font-weight: 600; font-size: 14px; color: var(--text-heading); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .vd-as-row-sub { font-size: 12px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      `}</style>
    </li>
  );
};

export default SearchAutosuggest;
